import * as BABYLON from "babylonjs";

const FIELD_WIDTH = 40;
const FIELD_LENGTH = 60;

export class RoundedStadium {
    public static crowdExcitement = 0;
    public static goalCelebrationTimer = 0;

    private static _audioCtx: AudioContext | null = null;
    private static _whiteNoise: AudioBufferSourceNode | null = null;
    private static _gainNode: GainNode | null = null;

    private static _crowdInstances: BABYLON.Mesh[] = [];
    private static _crowdSourceMeshes: BABYLON.Mesh[] = [];
    private static _banners: BABYLON.Mesh[] = [];
    private static _flags: BABYLON.InstancedMesh[] = [];
    private static _variations: any[] = [];
    private static _baseMatricesPerVar: Float32Array[] = [];

    public static async loadCrowdModel(scene: BABYLON.Scene): Promise<void> {
        try {
            const result = await BABYLON.SceneLoader.ImportMeshAsync("", "models/", "crowd_humans.gltf", scene);
            const root = result.meshes[0];

            const jerseyColors = [
                new BABYLON.Color3(0.9, 0.05, 0.05), // Red
                new BABYLON.Color3(0.95, 0.95, 0.95), // White
                new BABYLON.Color3(0.05, 0.05, 0.8)   // Blue
            ];

            for (let i = 0; i < 6; i++) {
                const name = `CrowdHuman_${i}`;
                const originalMesh = scene.getMeshByName(name);
                if (originalMesh && originalMesh instanceof BABYLON.Mesh) {
                    originalMesh.setParent(null);
                    originalMesh.isVisible = false;
                    originalMesh.position.set(0, 0, 0);
                    originalMesh.rotationQuaternion = BABYLON.Quaternion.Identity();

                    // Create 3 color variations for each human model
                    for (let j = 0; j < jerseyColors.length; j++) {
                        const varMesh = originalMesh.clone(`${name}_var_${j}`, null) as BABYLON.Mesh;
                        varMesh.isVisible = false;

                        // Find and update jersey material
                        if (varMesh.material instanceof BABYLON.MultiMaterial) {
                            const multi = varMesh.material.clone(`${varMesh.material.name}_${j}`);
                            for (let s = 0; s < multi.subMaterials.length; s++) {
                                const mat = multi.subMaterials[s];
                                if (mat && mat.name.toLowerCase().includes("jersey")) {
                                    const newMat = mat.clone(`${mat.name}_color_${j}`);
                                    if (newMat instanceof BABYLON.PBRMaterial) {
                                        newMat.albedoColor = jerseyColors[j];
                                    } else if (newMat instanceof BABYLON.StandardMaterial) {
                                        newMat.diffuseColor = jerseyColors[j];
                                    }
                                    multi.subMaterials[s] = newMat;
                                }
                            }
                            varMesh.material = multi;
                        } else if (varMesh.material) {
                            if (varMesh.material.name.toLowerCase().includes("jersey")) {
                                const newMat = varMesh.material.clone(`${varMesh.material.name}_color_${j}`);
                                if (newMat instanceof BABYLON.PBRMaterial) {
                                    newMat.albedoColor = jerseyColors[j];
                                } else if (newMat instanceof BABYLON.StandardMaterial) {
                                    newMat.diffuseColor = jerseyColors[j];
                                }
                                varMesh.material = newMat;
                            }
                        }
                        RoundedStadium._crowdSourceMeshes.push(varMesh);
                    }
                    originalMesh.dispose(false, true);
                }
            }
            root.dispose();
            console.log(`Loaded crowd model with ${RoundedStadium._crowdSourceMeshes.length} variations.`);
        } catch (e) {
            console.error("Failed to load crowd model:", e);
        }
    }

    public static initAudio(): void {
        if (RoundedStadium._audioCtx) return;

        try {
            RoundedStadium._audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const bufferSize = 2 * RoundedStadium._audioCtx.sampleRate;
            const noiseBuffer = RoundedStadium._audioCtx.createBuffer(1, bufferSize, RoundedStadium._audioCtx.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                output[i] = Math.random() * 2 - 1;
            }

            RoundedStadium._whiteNoise = RoundedStadium._audioCtx.createBufferSource();
            RoundedStadium._whiteNoise.buffer = noiseBuffer;
            RoundedStadium._whiteNoise.loop = true;

            // Filter to make it sound more like a crowd
            const filter = RoundedStadium._audioCtx.createBiquadFilter();
            filter.type = "lowpass";
            filter.frequency.value = 800;
            filter.Q.value = 1;

            RoundedStadium._gainNode = RoundedStadium._audioCtx.createGain();
            RoundedStadium._gainNode.gain.value = 0;

            RoundedStadium._whiteNoise.connect(filter);
            filter.connect(RoundedStadium._gainNode);
            RoundedStadium._gainNode.connect(RoundedStadium._audioCtx.destination);

            RoundedStadium._whiteNoise.start();
        } catch (e) {
            console.warn("Audio initialization failed:", e);
        }
    }

    public static update(dt: number, camRotationSpeed: number): void {
        // Initialize audio if needed
        if (!RoundedStadium._audioCtx) {
            RoundedStadium.initAudio();
        }

        // Update audio volume
        if (RoundedStadium._audioCtx && RoundedStadium._gainNode) {
            if (RoundedStadium._audioCtx.state === "suspended") {
                RoundedStadium._audioCtx.resume();
            }
            const targetGain = 0.02 + RoundedStadium.crowdExcitement * 0.15;
            RoundedStadium._gainNode.gain.setTargetAtTime(targetGain, RoundedStadium._audioCtx.currentTime, 0.1);
        }

        // Update excitement
        if (RoundedStadium.goalCelebrationTimer > 0) {
            RoundedStadium.goalCelebrationTimer -= dt;
            RoundedStadium.crowdExcitement = Math.max(0.3, RoundedStadium.goalCelebrationTimer / 5.0);
        } else {
            RoundedStadium.crowdExcitement = Math.max(0, RoundedStadium.crowdExcitement - dt * 0.2);
        }

        // optimize: stop animation if camera moving fast
        if (camRotationSpeed > 60) return;
        if (RoundedStadium._crowdInstances.length > 0 && !RoundedStadium._crowdInstances[0].isVisible) return;
        if (RoundedStadium.crowdExcitement < 0.05 && Math.random() > 0.1) return; // Reduce updates when calm

        const time = performance.now() * 0.001;

        // Flag waving
        const flagSpeed = 3 + RoundedStadium.crowdExcitement * 5;
        const flagIntensity = 0.2 + RoundedStadium.crowdExcitement * 0.5;
        for (let i = 0; i < RoundedStadium._flags.length; i++) {
            RoundedStadium._flags[i].rotation.z = Math.sin(time * flagSpeed + i) * flagIntensity;
            RoundedStadium._flags[i].rotation.x = Math.cos(time * (flagSpeed * 0.7) + i) * (flagIntensity * 0.5);
        }

        // Crowd wave / jumps
        const celebrationJump = RoundedStadium.crowdExcitement * 1.5;
        const isCelebrating = RoundedStadium.goalCelebrationTimer > 0;
        const tmpMat = new BABYLON.Matrix();
        let globalIdx = 0;

        for (let v = 0; v < RoundedStadium._variations.length; v++) {
            const vari = RoundedStadium._variations[v];
            if (vari.count === 0) continue;
            const baseBuf = RoundedStadium._baseMatricesPerVar[v];
            let dirty = false;

            for (let i = 0; i < vari.count; i++) {
                const idx = globalIdx++;
                // Optimization: skip some fans
                if (!isCelebrating && RoundedStadium.crowdExcitement < 0.2 && (idx * 3) % 4 !== 0) continue;

                const section = Math.floor(idx / 200);
                const sectionWave = Math.sin(time * (2.5 + RoundedStadium.crowdExcitement * 2) + section);

                let jump = 0;
                if (sectionWave > 0.7 || isCelebrating) {
                    const waveJump = sectionWave > 0.7 ? (sectionWave - 0.7) * 0.6 : 0;
                    const chaos = Math.sin(time * 10 + idx) * celebrationJump;
                    jump = waveJump + Math.max(0, chaos);
                }

                if (jump > 0 || isCelebrating || RoundedStadium.crowdExcitement > 0.1) {
                    const off = i * 16;
                    BABYLON.Matrix.FromArrayToRef(baseBuf, off, tmpMat);

                    // Celebration pulse
                    const pulse = 1 + jump * 0.2;
                    const m = (tmpMat as any).m;
                    m[0] = baseBuf[off] * pulse;
                    m[5] = baseBuf[off + 5] * pulse;
                    m[10] = baseBuf[off + 10] * pulse;

                    tmpMat.setTranslationFromFloats(baseBuf[off + 12], vari.baseYs[i] + jump, baseBuf[off + 14]);
                    vari.mesh.thinInstanceSetMatrixAt(i, tmpMat, false);
                    dirty = true;
                } else {
                    // Reset to base if not moving (optional, but good for stability)
                    const off = i * 16;
                    BABYLON.Matrix.FromArrayToRef(baseBuf, off, tmpMat);
                    vari.mesh.thinInstanceSetMatrixAt(i, tmpMat, false);
                }
            }
            if (dirty) {
                vari.mesh.thinInstanceBufferUpdated("matrix");
            }
        }
    }

    public static triggerGoalCelebration(): void {
        RoundedStadium.initAudio();
        RoundedStadium.crowdExcitement = 1.0;
        RoundedStadium.goalCelebrationTimer = 5.0;
    }

    public static setCrowdVisible(visible: boolean): void {
        RoundedStadium._crowdInstances.forEach(m => m.isVisible = visible);
    }

    // --- CREATION METHODS ---

    public static create(scene: BABYLON.Scene): void {
        RoundedStadium.createRoundedStadiumBowl(scene);
        RoundedStadium.createRoundedSidelineArea(scene);
        RoundedStadium.createRoundedCrowd(scene);
        RoundedStadium._createBanners(scene);
    }

    private static _createBanners(scene: BABYLON.Scene): void {
        const bannerMat = new BABYLON.StandardMaterial("bannerMat", scene);
        bannerMat.diffuseColor = new BABYLON.Color3(0.1, 0.1, 0.15);

        const bannerColors = [
            new BABYLON.Color3(0.8, 0.1, 0.1), // Red
            new BABYLON.Color3(0.1, 0.1, 0.7), // Blue
            new BABYLON.Color3(0.9, 0.8, 0.1), // Yellow
        ];

        const tiers = [
            { y: 2, offset: 10 },
            { y: 8, offset: 22 }
        ];

        tiers.forEach((tier, tIdx) => {
            const path = RoundedStadium._getRoundedPath(tier.offset - 2.05, tier.y - 1.2);
            const bannerHeight = 1.0;
            const bannerProfile = [
                new BABYLON.Vector3(0, 0, 0),
                new BABYLON.Vector3(0.05, 0, 0),
                new BABYLON.Vector3(0.05, bannerHeight, 0),
                new BABYLON.Vector3(0, bannerHeight, 0),
                new BABYLON.Vector3(0, 0, 0)
            ];

            const banners = BABYLON.MeshBuilder.ExtrudeShape(`banners_${tIdx}`, {
                shape: bannerProfile, path: path, closePath: true, cap: BABYLON.Mesh.CAP_ALL
            }, scene);

            banners.material = bannerMat;

            // Add some colorful sections to banners
            const tex = new BABYLON.DynamicTexture(`bannerTex_${tIdx}`, { width: 1024, height: 64 }, scene);
            const ctx = tex.getContext() as any;
            for (let i = 0; i < 16; i++) {
                const color = bannerColors[i % bannerColors.length];
                ctx.fillStyle = color.toHexString();
                ctx.fillRect(i * 64, 0, 64, 64);

                // Add some text
                ctx.fillStyle = "white";
                ctx.font = "bold 40px Arial";
                ctx.textAlign = "center";
                ctx.fillText(i % 2 === 0 ? "AMN" : "GOAL", i * 64 + 32, 45);
            }
            tex.update();

            const mat = new BABYLON.StandardMaterial(`bannerMatInst_${tIdx}`, scene);
            mat.diffuseTexture = tex;
            mat.emissiveColor = new BABYLON.Color3(0.2, 0.2, 0.2);
            banners.material = mat;
            banners.freezeWorldMatrix();
        });
    }

    private static _getRoundedPath(radiusOffset: number, y: number): BABYLON.Vector3[] {
        const path: BABYLON.Vector3[] = [];
        const r = 15 + radiusOffset; // Baseline radius 15 for corners
        const segs = 12;

        // 1. Right Straight (Z: -30 to 30)
        path.push(new BABYLON.Vector3(20 + r, y, -30));
        path.push(new BABYLON.Vector3(20 + r, y, 30));

        // 2. Front-Right Corner
        for (let i = 1; i <= segs; i++) {
            const ang = (i / segs) * (Math.PI / 2);
            path.push(new BABYLON.Vector3(20 + r * Math.cos(ang), y, 30 + r * Math.sin(ang)));
        }
        // 3. Front Straight
        path.push(new BABYLON.Vector3(-20, y, 30 + r));

        // 4. Front-Left Corner
        for (let i = 1; i <= segs; i++) {
            const ang = Math.PI / 2 + (i / segs) * (Math.PI / 2);
            path.push(new BABYLON.Vector3(-20 + r * Math.cos(ang), y, 30 + r * Math.sin(ang)));
        }
        // 5. Left Straight
        path.push(new BABYLON.Vector3(-20 - r, y, -30));

        // 6. Back-Left Corner
        for (let i = 1; i <= segs; i++) {
            const ang = Math.PI + (i / segs) * (Math.PI / 2);
            path.push(new BABYLON.Vector3(-20 + r * Math.cos(ang), y, -30 + r * Math.sin(ang)));
        }
        // 7. Back Straight
        path.push(new BABYLON.Vector3(20, y, -30 - r));

        // 8. Back-Right Corner
        for (let i = 1; i <= segs; i++) {
            const ang = 3 * Math.PI / 2 + (i / segs) * (Math.PI / 2);
            path.push(new BABYLON.Vector3(20 + r * Math.cos(ang), y, -30 + r * Math.sin(ang)));
        }
        path.push(path[0].clone());
        return path;
    }

    public static createRoundedStadiumBowl(scene: BABYLON.Scene): void {
        const concreteMat = new BABYLON.StandardMaterial("bowlConcrete", scene);
        concreteMat.diffuseColor = new BABYLON.Color3(0.55, 0.53, 0.52);

        const seatRedMat = new BABYLON.StandardMaterial("bowlSeatsRed", scene);
        seatRedMat.diffuseColor = new BABYLON.Color3(0.7, 0.08, 0.1);
        const seatBlueMat = new BABYLON.StandardMaterial("bowlSeatsBlue", scene);
        seatBlueMat.diffuseColor = new BABYLON.Color3(0.1, 0.12, 0.6);

        const roofMat = new BABYLON.StandardMaterial("bowlRoof", scene);
        roofMat.diffuseColor = new BABYLON.Color3(0.25, 0.25, 0.28);
        roofMat.alpha = 0.9;
        roofMat.backFaceCulling = false;

        const tiers = [
            { y: 2, depth: 12, offset: 10, rows: 6 },
            { y: 8, depth: 14, offset: 22, rows: 8 },
            { y: 16, depth: 18, offset: 36, rows: 6 }
        ];

        const freeze = (m: BABYLON.Mesh) => {
            m.receiveShadows = true; m.freezeWorldMatrix();
        };

        for (let t = 0; t < tiers.length; t++) {
            const tier = tiers[t];
            const tierDepth = tier.rows * 0.8 + 2;
            const slabH = Math.max(1, tier.y - 0.5);

            const slabProfile = [
                new BABYLON.Vector3(0, 0, 0), new BABYLON.Vector3(tierDepth, 0, 0),
                new BABYLON.Vector3(tierDepth, slabH, 0), new BABYLON.Vector3(0, slabH, 0), new BABYLON.Vector3(0, 0, 0)
            ];
            const path = RoundedStadium._getRoundedPath(tier.offset - 2, 0);
            const slab = BABYLON.MeshBuilder.ExtrudeShape(`bowlSlab_${t}`, {
                shape: slabProfile, path: path, sideOrientation: BABYLON.Mesh.DOUBLESIDE, cap: BABYLON.Mesh.CAP_ALL, closeShape: true, closePath: true
            }, scene);
            slab.material = concreteMat;
            freeze(slab);

            const seatColor = (t === 1) ? seatBlueMat : seatRedMat;
            for (let r = 0; r < tier.rows; r++) {
                const stepY = tier.y + (r / tier.rows) * 6;
                const stepDepthOffset = r * 0.8;
                const seatProfile = [
                    new BABYLON.Vector3(stepDepthOffset, stepY - 0.3, 0),
                    new BABYLON.Vector3(stepDepthOffset + 0.75, stepY - 0.3, 0),
                    new BABYLON.Vector3(stepDepthOffset + 0.75, stepY - 0.3 + 0.12, 0),
                    new BABYLON.Vector3(stepDepthOffset, stepY - 0.3 + 0.12, 0),
                    new BABYLON.Vector3(stepDepthOffset, stepY - 0.3, 0)
                ];
                const seatMesh = BABYLON.MeshBuilder.ExtrudeShape(`bowlRow_${t}_${r}`, {
                    shape: seatProfile, path: path, sideOrientation: BABYLON.Mesh.DOUBLESIDE, closePath: true, cap: BABYLON.Mesh.CAP_ALL
                }, scene);
                seatMesh.material = seatColor;
                freeze(seatMesh);
            }

            const innerProfile = [new BABYLON.Vector3(-0.3, 0, 0), new BABYLON.Vector3(0, 0, 0), new BABYLON.Vector3(0, Math.max(0.5, tier.y - 0.5), 0), new BABYLON.Vector3(-0.3, Math.max(0.5, tier.y - 0.5), 0), new BABYLON.Vector3(-0.3, 0, 0)];
            const innerWall = BABYLON.MeshBuilder.ExtrudeShape(`bowlInner_${t}`, {
                shape: innerProfile, path: path, closePath: true, cap: BABYLON.Mesh.CAP_ALL
            }, scene);
            innerWall.material = concreteMat;
            freeze(innerWall);
        }

        // Outer/Roof
        const upper = tiers[tiers.length - 1];
        const outerH = upper.y + 9;
        const outerRadius = upper.offset + upper.rows * 0.8 + 2;
        const outerProfile = [new BABYLON.Vector3(0, 0, 0), new BABYLON.Vector3(1, 0, 0), new BABYLON.Vector3(1, outerH, 0), new BABYLON.Vector3(0, outerH, 0), new BABYLON.Vector3(0, 0, 0)];
        const outerWall = BABYLON.MeshBuilder.ExtrudeShape(`bowlOuter`, { shape: outerProfile, path: RoundedStadium._getRoundedPath(outerRadius, 0), closePath: true, cap: BABYLON.Mesh.CAP_ALL }, scene);
        outerWall.material = concreteMat;
        freeze(outerWall);

        const roofProfile = [new BABYLON.Vector3(-15, outerH + 0.5, 0), new BABYLON.Vector3(1, outerH + 2, 0), new BABYLON.Vector3(1, outerH + 2.2, 0), new BABYLON.Vector3(-15, outerH + 0.7, 0), new BABYLON.Vector3(-15, outerH + 0.5, 0)];
        const roof = BABYLON.MeshBuilder.ExtrudeShape(`bowlRoof`, { shape: roofProfile, path: RoundedStadium._getRoundedPath(outerRadius, 0), closePath: true }, scene);
        roof.material = roofMat;
        freeze(roof);
    }

    public static createRoundedSidelineArea(scene: BABYLON.Scene): void {
        const surroundMat = new BABYLON.StandardMaterial("surroundMat", scene);
        surroundMat.diffuseColor = new BABYLON.Color3(0.28, 0.38, 0.22);
        const ground = BABYLON.MeshBuilder.CreateBox("surroundGround", { width: 150, height: 0.1, depth: 180 }, scene);
        ground.position.y = -0.15;
        ground.material = surroundMat;
        ground.receiveShadows = true;
        ground.freezeWorldMatrix();

        const apronMat = new BABYLON.StandardMaterial("apronMat", scene);
        apronMat.diffuseColor = new BABYLON.Color3(0.55, 0.42, 0.35);
        const innerPath = RoundedStadium._getRoundedPath(-10, 0.02);
        const outerPath = RoundedStadium._getRoundedPath(-6, 0.02);
        const track = BABYLON.MeshBuilder.CreateRibbon("trackRibbon", {
            pathArray: [innerPath, outerPath], closePath: true, sideOrientation: BABYLON.Mesh.DOUBLESIDE
        }, scene);
        track.material = apronMat;
        track.receiveShadows = true;
        track.freezeWorldMatrix();

        RoundedStadium._createDugouts(scene);
        RoundedStadium._createSurroundingDetails(scene);
        RoundedStadium._createInvisibleWalls(scene);

        const flagPoleMat = new BABYLON.StandardMaterial("flagPoleMat", scene);
        flagPoleMat.diffuseColor = new BABYLON.Color3(0.9, 0.9, 0.1);
        const cornerFlagMat = new BABYLON.StandardMaterial("cornerFlagTriMat", scene);
        cornerFlagMat.diffuseColor = new BABYLON.Color3(0.9, 0.1, 0.1);
        cornerFlagMat.backFaceCulling = false;
        const halfW = FIELD_WIDTH / 2;
        const halfL = FIELD_LENGTH / 2;
        for (const cx of [-1, 1]) {
            for (const cz of [-1, 1]) {
                const pole = BABYLON.MeshBuilder.CreateCylinder(`cornerPole_${cx}_${cz}`, { height: 1.5, diameter: 0.04 }, scene);
                pole.position.set(cx * halfW, 0.75, cz * halfL);
                pole.material = flagPoleMat;
                pole.freezeWorldMatrix();
                const flag = BABYLON.MeshBuilder.CreatePlane(`cornerFlag_${cx}_${cz}`, { width: 0.35, height: 0.25, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
                flag.position.set(cx * halfW + cx * 0.17, 1.4, cz * halfL);
                flag.material = cornerFlagMat;
                flag.freezeWorldMatrix();
            }
        }
    }

    private static _createDugouts(scene: BABYLON.Scene): void {
        const benchMat = new BABYLON.StandardMaterial("benchMat", scene);
        benchMat.diffuseColor = new BABYLON.Color3(0.15, 0.15, 0.18);
        const benchSeatMat = new BABYLON.StandardMaterial("benchSeatMat", scene);
        benchSeatMat.diffuseColor = new BABYLON.Color3(0.8, 0.08, 0.1);
        const benchSeatBlueMat = new BABYLON.StandardMaterial("benchSeatBlueMat", scene);
        benchSeatBlueMat.diffuseColor = new BABYLON.Color3(0.08, 0.1, 0.7);

        for (const side of [-1, 1]) {
            const dugoutX = side * (FIELD_WIDTH / 2 + 6);
            const dugoutZ = -6;
            const dugoutLen = 12;
            const seatColor = side === -1 ? benchSeatMat : benchSeatBlueMat;

            const roof = BABYLON.MeshBuilder.CreateBox(`dugoutRoof_${side}`, { width: 3, height: 0.1, depth: dugoutLen + 1 }, scene);
            roof.position.set(dugoutX, 2.2, dugoutZ);
            roof.material = benchMat;
            roof.freezeWorldMatrix();

            for (let i = 0; i < 8; i++) {
                const seat = BABYLON.MeshBuilder.CreateBox(`seat_${side}_${i}`, { width: 0.5, height: 0.1, depth: 0.5 }, scene);
                seat.position.set(dugoutX + side * 0.5, 0.6, dugoutZ - dugoutLen / 2 + 1 + i * 1.4);
                seat.material = seatColor;
                seat.freezeWorldMatrix();
            }
        }
    }

    private static _createSurroundingDetails(scene: BABYLON.Scene): void {
        // ── shared materials ───────────────────────────────────────────────────
        const whiteMat = new BABYLON.StandardMaterial("sdWhiteMat", scene);
        whiteMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.95);

        const darkMat = new BABYLON.StandardMaterial("sdDarkMat", scene);
        darkMat.diffuseColor = new BABYLON.Color3(0.12, 0.12, 0.14);

        const metalMat = new BABYLON.StandardMaterial("sdMetalMat", scene);
        metalMat.diffuseColor = new BABYLON.Color3(0.55, 0.55, 0.6);
        metalMat.specularColor = new BABYLON.Color3(0.8, 0.8, 0.8);

        const yellowMat = new BABYLON.StandardMaterial("sdYellowMat", scene);
        yellowMat.diffuseColor = new BABYLON.Color3(0.95, 0.85, 0.05);

        const redMat = new BABYLON.StandardMaterial("sdRedMat", scene);
        redMat.diffuseColor = new BABYLON.Color3(0.8, 0.08, 0.08);

        const blueMat = new BABYLON.StandardMaterial("sdBlueMat", scene);
        blueMat.diffuseColor = new BABYLON.Color3(0.08, 0.1, 0.7);

        const woodMat = new BABYLON.StandardMaterial("sdWoodMat", scene);
        woodMat.diffuseColor = new BABYLON.Color3(0.55, 0.38, 0.22);

        const glassMat = new BABYLON.StandardMaterial("sdGlassMat", scene);
        glassMat.diffuseColor = new BABYLON.Color3(0.6, 0.8, 0.9);
        glassMat.alpha = 0.35;
        glassMat.backFaceCulling = false;

        const freeze = (m: BABYLON.Mesh) => { m.freezeWorldMatrix(); };

        // ── 1. ADVERTISING BOARDS along both touchlines ───────────────────────
        //   Each board: flat panel, ~2.4 m wide, ~0.9 m tall, at pitch edge + 0.5 m
        const adColors = [
            new BABYLON.Color3(0.9, 0.15, 0.15),  // red brand
            new BABYLON.Color3(0.1, 0.2, 0.85),   // blue brand
            new BABYLON.Color3(0.15, 0.65, 0.15),  // green brand
            new BABYLON.Color3(0.95, 0.85, 0.05),  // yellow brand
            new BABYLON.Color3(0.0, 0.0, 0.0),    // black brand
        ];
        const boardW = 2.4;
        const boardH = 0.85;
        const boardT = 0.08;
        const halfW = FIELD_WIDTH / 2;   // 20
        const halfL = FIELD_LENGTH / 2;  // 30
        const boardOffsetX = halfW + 1.0; // just outside the touchline

        // along each long side (Z from -halfL+1 to halfL-1) place boards
        for (const sideX of [-1, 1]) {
            const totalLen = FIELD_LENGTH - 2; // leave gap at ends
            const count = Math.floor(totalLen / (boardW + 0.1));
            const step = totalLen / count;
            for (let i = 0; i < count; i++) {
                const z = -halfL + 1 + i * step + boardW / 2;
                const color = adColors[i % adColors.length];
                const adMat = new BABYLON.StandardMaterial(`adMat_${sideX}_${i}`, scene);
                adMat.diffuseColor = color;
                adMat.emissiveColor = color.scale(0.18);

                const board = BABYLON.MeshBuilder.CreateBox(`adBoard_${sideX}_${i}`, {
                    width: boardT, height: boardH, depth: boardW
                }, scene);
                board.position.set(sideX * boardOffsetX, boardH / 2, z);
                board.material = adMat;
                freeze(board);

                // thin white border frame
                const frame = BABYLON.MeshBuilder.CreateBox(`adFrame_${sideX}_${i}`, {
                    width: boardT + 0.02, height: boardH + 0.08, depth: boardW + 0.08
                }, scene);
                frame.position.set(sideX * boardOffsetX, boardH / 2, z);
                // hollow trick: use a slightly larger white box behind
                const frameMat = new BABYLON.StandardMaterial(`adFrameMat_${sideX}_${i}`, scene);
                frameMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.95);
                frame.material = frameMat;
                frame.position.x += sideX * 0.005; // push 1 mm behind board face
                freeze(frame);
            }
        }

        // boards along the goal-lines (shorter, behind goals)
        for (const endZ of [-1, 1]) {
            const goalGap = 7.4;
            const sideSections = [-1, 1];
            const sectionLen = (FIELD_WIDTH - goalGap) / 2 - 0.5; // ~5.8 m per side
            for (const lr of sideSections) {
                const centerX = lr * (goalGap / 2 + sectionLen / 2 + 0.25);
                const count2 = Math.max(1, Math.floor(sectionLen / (boardW + 0.1)));
                const step2 = sectionLen / count2;
                for (let i = 0; i < count2; i++) {
                    const x = centerX - lr * (sectionLen / 2) + lr * (i * step2 + boardW / 2);
                    const color2 = adColors[(i + 2) % adColors.length];
                    const adMat2 = new BABYLON.StandardMaterial(`adMatEnd_${endZ}_${lr}_${i}`, scene);
                    adMat2.diffuseColor = color2;
                    adMat2.emissiveColor = color2.scale(0.18);
                    const board2 = BABYLON.MeshBuilder.CreateBox(`adBoardEnd_${endZ}_${lr}_${i}`, {
                        width: boardW, height: boardH, depth: boardT
                    }, scene);
                    board2.position.set(x, boardH / 2, endZ * (halfL + 0.8));
                    board2.material = adMat2;
                    freeze(board2);
                }
            }
        }

        // ── 2. FLOODLIGHT TOWERS in the four quadrant corners of the arena ─────
        //   Tall pole + cross-arm + light head cluster
        const towerPositions = [
            { x: 28, z: 38 }, { x: -28, z: 38 },
            { x: 28, z: -38 }, { x: -28, z: -38 },
        ];
        for (let t = 0; t < towerPositions.length; t++) {
            const tp = towerPositions[t];

            // Main mast
            const mast = BABYLON.MeshBuilder.CreateCylinder(`flMast_${t}`, {
                height: 28, diameterTop: 0.25, diameterBottom: 0.55, tessellation: 8
            }, scene);
            mast.position.set(tp.x, 14, tp.z);
            mast.material = metalMat;
            freeze(mast);

            // Horizontal boom
            const boom = BABYLON.MeshBuilder.CreateBox(`flBoom_${t}`, {
                width: 4, height: 0.2, depth: 0.2
            }, scene);
            boom.position.set(tp.x, 28, tp.z);
            boom.material = metalMat;
            freeze(boom);

            // Light heads (3 per tower)
            for (let h = 0; h < 3; h++) {
                const lx = tp.x + (h - 1) * 1.2;
                const head = BABYLON.MeshBuilder.CreateBox(`flHead_${t}_${h}`, {
                    width: 0.9, height: 0.35, depth: 0.5
                }, scene);
                head.position.set(lx, 27.9, tp.z);
                const headMat = new BABYLON.StandardMaterial(`flHeadMat_${t}_${h}`, scene);
                headMat.diffuseColor = new BABYLON.Color3(0.15, 0.15, 0.15);
                headMat.emissiveColor = new BABYLON.Color3(0.7, 0.7, 0.6);
                head.material = headMat;
                freeze(head);
            }

            // Base plinth
            const base = BABYLON.MeshBuilder.CreateBox(`flBase_${t}`, {
                width: 1.2, height: 0.4, depth: 1.2
            }, scene);
            base.position.set(tp.x, 0.2, tp.z);
            base.material = darkMat;
            freeze(base);
        }

        // ── 3. IMPROVED DUGOUTS – back wall + glass front + side walls ─────────
        const dugoutColors = [redMat, blueMat];
        for (let sd = 0; sd < 2; sd++) {
            const side = sd === 0 ? -1 : 1;
            const dugoutX = side * (halfW + 6);
            const dugoutZ = -6;
            const len = 13;
            const depth = 3.2;
            const height = 2.3;
            const seatCol = dugoutColors[sd];

            // Back wall (concrete)
            const backWall = BABYLON.MeshBuilder.CreateBox(`dugBack_${side}`, {
                width: depth, height: height, depth: len
            }, scene);
            backWall.position.set(dugoutX + side * (depth / 2), height / 2, dugoutZ);
            backWall.material = darkMat;
            freeze(backWall);

            // Side walls
            for (const end of [-1, 1]) {
                const sw = BABYLON.MeshBuilder.CreateBox(`dugSideWall_${side}_${end}`, {
                    width: depth, height: height, depth: 0.15
                }, scene);
                sw.position.set(dugoutX, height / 2, dugoutZ + end * len / 2);
                sw.material = darkMat;
                freeze(sw);
            }

            // Glass front panel (facing the pitch)
            const glass = BABYLON.MeshBuilder.CreateBox(`dugGlass_${side}`, {
                width: 0.08, height: height - 0.3, depth: len - 0.3
            }, scene);
            glass.position.set(dugoutX - side * (depth / 2 - 0.04), (height - 0.3) / 2 + 0.3, dugoutZ);
            glass.material = glassMat;
            freeze(glass);

            // Colored stripe on roof edge
            const roofStripe = BABYLON.MeshBuilder.CreateBox(`dugRoofStripe_${side}`, {
                width: depth + 0.1, height: 0.12, depth: len + 0.1
            }, scene);
            roofStripe.position.set(dugoutX, height + 0.06, dugoutZ);
            roofStripe.material = seatCol;
            freeze(roofStripe);

            // Seat backs (thin vertical slab behind each seat)
            for (let i = 0; i < 8; i++) {
                const sz = dugoutZ - len / 2 + 1 + i * 1.5;
                const back = BABYLON.MeshBuilder.CreateBox(`dugSeatBack_${side}_${i}`, {
                    width: 0.06, height: 0.55, depth: 0.48
                }, scene);
                back.position.set(dugoutX + side * 0.55, 0.85, sz);
                back.material = seatCol;
                freeze(back);
            }

            // Water bottle rack at dugout entrance (front end)
            const rackZ = dugoutZ + len / 2 + 0.8;
            const rack = BABYLON.MeshBuilder.CreateBox(`waterRack_${side}`, {
                width: 0.4, height: 0.5, depth: 1.2
            }, scene);
            rack.position.set(dugoutX, 0.25, rackZ);
            rack.material = woodMat;
            freeze(rack);

            // Bottles on rack (small cylinders)
            for (let b = 0; b < 5; b++) {
                const bottle = BABYLON.MeshBuilder.CreateCylinder(`bottle_${side}_${b}`, {
                    height: 0.28, diameter: 0.09, tessellation: 8
                }, scene);
                bottle.position.set(dugoutX + (b - 2) * 0.1, 0.64, rackZ);
                const botMat = new BABYLON.StandardMaterial(`botMat_${side}_${b}`, scene);
                botMat.diffuseColor = new BABYLON.Color3(0.3, 0.7, 0.95);
                botMat.alpha = 0.75;
                bottle.material = botMat;
                freeze(bottle);
            }
        }

        // ── 4. FOURTH-OFFICIAL TABLE at halfway line (right sideline) ─────────
        const foX = halfW + 2.2;
        const foZ = 0;

        // Table top
        const foTable = BABYLON.MeshBuilder.CreateBox("foTable", {
            width: 0.15, height: 0.05, depth: 1.8
        }, scene);
        foTable.position.set(foX, 0.8, foZ);
        foTable.material = whiteMat;
        freeze(foTable);

        // Table legs
        for (const lz of [-0.7, 0.7]) {
            const leg = BABYLON.MeshBuilder.CreateBox(`foLeg_${lz}`, {
                width: 0.06, height: 0.8, depth: 0.06
            }, scene);
            leg.position.set(foX, 0.4, lz);
            leg.material = metalMat;
            freeze(leg);
        }

        // Electronic scoreboard on table
        const foBoard = BABYLON.MeshBuilder.CreateBox("foBoard", {
            width: 0.06, height: 0.5, depth: 0.4
        }, scene);
        foBoard.position.set(foX, 1.05, foZ);
        const foBoardMat = new BABYLON.StandardMaterial("foBoardMat", scene);
        foBoardMat.diffuseColor = new BABYLON.Color3(0.05, 0.05, 0.05);
        foBoardMat.emissiveColor = new BABYLON.Color3(0.05, 0.2, 0.02);
        foBoard.material = foBoardMat;
        freeze(foBoard);

        // Chairs for officials (2 chairs behind table)
        for (const cz of [-0.4, 0.4]) {
            const chairBase = BABYLON.MeshBuilder.CreateBox(`foChair_${cz}`, {
                width: 0.08, height: 0.06, depth: 0.42
            }, scene);
            chairBase.position.set(foX + 0.35, 0.48, cz);
            chairBase.material = darkMat;
            freeze(chairBase);

            const chairBack = BABYLON.MeshBuilder.CreateBox(`foChairBack_${cz}`, {
                width: 0.06, height: 0.4, depth: 0.42
            }, scene);
            chairBack.position.set(foX + 0.55, 0.7, cz);
            chairBack.material = darkMat;
            freeze(chairBack);
        }

        // ── 5. BALL-BOY STOOLS near each corner (inside the apron) ────────────
        const ballBoyPositions = [
            { x: halfW + 1.5, z: halfL + 1.5 },
            { x: -halfW - 1.5, z: halfL + 1.5 },
            { x: halfW + 1.5, z: -halfL - 1.5 },
            { x: -halfW - 1.5, z: -halfL - 1.5 },
        ];
        for (let bb = 0; bb < ballBoyPositions.length; bb++) {
            const bp = ballBoyPositions[bb];
            // Small stool seat
            const stool = BABYLON.MeshBuilder.CreateCylinder(`bbStool_${bb}`, {
                height: 0.06, diameter: 0.32, tessellation: 12
            }, scene);
            stool.position.set(bp.x, 0.45, bp.z);
            stool.material = yellowMat;
            freeze(stool);

            // Stool legs (3)
            for (let l = 0; l < 3; l++) {
                const ang = (l / 3) * Math.PI * 2;
                const leg = BABYLON.MeshBuilder.CreateCylinder(`bbLeg_${bb}_${l}`, {
                    height: 0.45, diameter: 0.04, tessellation: 6
                }, scene);
                leg.position.set(bp.x + Math.cos(ang) * 0.12, 0.225, bp.z + Math.sin(ang) * 0.12);
                leg.material = metalMat;
                freeze(leg);
            }

            // Spare ball on ground next to stool
            const spareBall = BABYLON.MeshBuilder.CreateSphere(`bbBall_${bb}`, {
                diameter: 0.22, segments: 8
            }, scene);
            spareBall.position.set(bp.x + 0.35, 0.11, bp.z);
            const bbBallMat = new BABYLON.StandardMaterial(`bbBallMat_${bb}`, scene);
            bbBallMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.95);
            spareBall.material = bbBallMat;
            freeze(spareBall);
        }

        // ── 6. MEDICAL STRETCHER near the side (left touchline midfield) ───────
        const stretcherX = -(halfW + 2.5);
        const stretcherZ = 5;

        const stretcherFrame = BABYLON.MeshBuilder.CreateBox("stretcher", {
            width: 0.5, height: 0.08, depth: 1.9
        }, scene);
        stretcherFrame.position.set(stretcherX, 0.55, stretcherZ);
        stretcherFrame.material = woodMat;
        freeze(stretcherFrame);

        // Red cross marker on top
        const rcH = BABYLON.MeshBuilder.CreateBox("rcH", { width: 0.04, height: 0.02, depth: 0.3 }, scene);
        rcH.position.set(stretcherX, 0.6, stretcherZ);
        rcH.material = redMat;
        freeze(rcH);
        const rcV = BABYLON.MeshBuilder.CreateBox("rcV", { width: 0.3, height: 0.02, depth: 0.04 }, scene);
        rcV.position.set(stretcherX, 0.6, stretcherZ);
        rcV.material = redMat;
        freeze(rcV);

        // Stretcher legs (fold-out style, 4 legs)
        for (const lz of [-0.7, 0.7]) {
            const legA = BABYLON.MeshBuilder.CreateBox(`strLeg_${lz}`, {
                width: 0.04, height: 0.55, depth: 0.04
            }, scene);
            legA.position.set(stretcherX - 0.2, 0.275, stretcherZ + lz);
            legA.material = metalMat;
            freeze(legA);
            const legB = legA.clone(`strLegB_${lz}`);
            legB.position.x = stretcherX + 0.2;
            freeze(legB);
        }

        // ── 7. CAMERA PLATFORM at halfway on the main stand side ─────────────
        const camPlatX = -(halfW + 3.5);
        const camPlatZ = 0;

        // Platform deck
        const deck = BABYLON.MeshBuilder.CreateBox("camDeck", {
            width: 1.0, height: 0.1, depth: 2.0
        }, scene);
        deck.position.set(camPlatX, 1.2, camPlatZ);
        deck.material = darkMat;
        freeze(deck);

        // Platform legs (scaffolding-style, 4 posts)
        for (const pz of [-0.8, 0.8]) {
            for (const px of [-0.35, 0.35]) {
                const post = BABYLON.MeshBuilder.CreateCylinder(`camPost_${px}_${pz}`, {
                    height: 1.2, diameter: 0.08, tessellation: 6
                }, scene);
                post.position.set(camPlatX + px, 0.6, camPlatZ + pz);
                post.material = metalMat;
                freeze(post);
            }
        }

        // TV Camera on platform (simple boxy shape)
        const camBody = BABYLON.MeshBuilder.CreateBox("tvCamBody", {
            width: 0.18, height: 0.22, depth: 0.55
        }, scene);
        camBody.position.set(camPlatX, 1.42, camPlatZ);
        camBody.material = darkMat;
        freeze(camBody);

        const camLens = BABYLON.MeshBuilder.CreateCylinder("tvCamLens", {
            height: 0.22, diameter: 0.12, tessellation: 10
        }, scene);
        camLens.rotation.z = Math.PI / 2;
        camLens.position.set(camPlatX - 0.38, 1.42, camPlatZ);
        const lensMat = new BABYLON.StandardMaterial("camLensMat", scene);
        lensMat.diffuseColor = new BABYLON.Color3(0.05, 0.05, 0.05);
        lensMat.emissiveColor = new BABYLON.Color3(0.0, 0.05, 0.15);
        camLens.material = lensMat;
        freeze(camLens);

        // ── 8. GOAL NET FRAMES (side poles + crossbar + net plane behind) ──────
        const netMat = new BABYLON.StandardMaterial("goalNetMat", scene);
        netMat.diffuseColor = new BABYLON.Color3(0.9, 0.9, 0.9);
        netMat.alpha = 0.45;
        netMat.backFaceCulling = false;
        netMat.wireframe = false;

        const goalPostMat = new BABYLON.StandardMaterial("goalPostMat", scene);
        goalPostMat.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.95);
        goalPostMat.specularColor = new BABYLON.Color3(0.8, 0.8, 0.8);

        const goalW = 7.32;
        const goalH = 2.44;
        const goalDepth = 2.0;

        for (const endZ of [-1, 1]) {
            const gz = endZ * halfL;

            // Left & right posts
            for (const postX of [-goalW / 2, goalW / 2]) {
                const post = BABYLON.MeshBuilder.CreateCylinder(`goalPost_${endZ}_${postX}`, {
                    height: goalH, diameter: 0.12, tessellation: 10
                }, scene);
                post.position.set(postX, goalH / 2, gz);
                post.material = goalPostMat;
                freeze(post);

                // Back-corner post (depth)
                const backPost = BABYLON.MeshBuilder.CreateCylinder(`goalBackPost_${endZ}_${postX}`, {
                    height: goalH, diameter: 0.10, tessellation: 10
                }, scene);
                backPost.position.set(postX, goalH / 2, gz + endZ * goalDepth);
                backPost.material = goalPostMat;
                freeze(backPost);

                // Lower diagonal support (ground → back)
                const supportLen = Math.sqrt(goalDepth * goalDepth + 0.3 * 0.3);
                const support = BABYLON.MeshBuilder.CreateCylinder(`goalSupport_${endZ}_${postX}`, {
                    height: supportLen, diameter: 0.07, tessellation: 6
                }, scene);
                support.position.set(postX, 0.15, gz + endZ * goalDepth / 2);
                support.rotation.x = -endZ * Math.atan2(0.3, goalDepth);
                support.material = goalPostMat;
                freeze(support);
            }

            // Crossbar
            const crossbar = BABYLON.MeshBuilder.CreateCylinder(`goalCrossbar_${endZ}`, {
                height: goalW, diameter: 0.12, tessellation: 10
            }, scene);
            crossbar.rotation.z = Math.PI / 2;
            crossbar.position.set(0, goalH, gz);
            crossbar.material = goalPostMat;
            freeze(crossbar);

            // Top back rail
            const topRail = BABYLON.MeshBuilder.CreateCylinder(`goalTopRail_${endZ}`, {
                height: goalW, diameter: 0.08, tessellation: 8
            }, scene);
            topRail.rotation.z = Math.PI / 2;
            topRail.position.set(0, goalH, gz + endZ * goalDepth);
            topRail.material = goalPostMat;
            freeze(topRail);

            // Net – back panel
            const backNet = BABYLON.MeshBuilder.CreatePlane(`goalBackNet_${endZ}`, {
                width: goalW, height: goalH, sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);
            backNet.position.set(0, goalH / 2, gz + endZ * goalDepth);
            backNet.material = netMat;
            freeze(backNet);

            // Net – top panel
            const topNet = BABYLON.MeshBuilder.CreatePlane(`goalTopNet_${endZ}`, {
                width: goalW, height: goalDepth, sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);
            topNet.rotation.x = Math.PI / 2;
            topNet.position.set(0, goalH, gz + endZ * goalDepth / 2);
            topNet.material = netMat;
            freeze(topNet);

            // Net – left side panel
            const leftNet = BABYLON.MeshBuilder.CreatePlane(`goalLeftNet_${endZ}`, {
                width: goalDepth, height: goalH, sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);
            leftNet.rotation.y = Math.PI / 2;
            leftNet.position.set(-goalW / 2, goalH / 2, gz + endZ * goalDepth / 2);
            leftNet.material = netMat;
            freeze(leftNet);

            // Net – right side panel
            const rightNet = BABYLON.MeshBuilder.CreatePlane(`goalRightNet_${endZ}`, {
                width: goalDepth, height: goalH, sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);
            rightNet.rotation.y = Math.PI / 2;
            rightNet.position.set(goalW / 2, goalH / 2, gz + endZ * goalDepth / 2);
            rightNet.material = netMat;
            freeze(rightNet);
        }

        // ── 9. SMALL GROUNDSKEEPING SHED near the far corner ─────────────────
        const shedX = 24;
        const shedZ = -36;

        const shedBase = BABYLON.MeshBuilder.CreateBox("shed", {
            width: 3.5, height: 2.5, depth: 5
        }, scene);
        shedBase.position.set(shedX, 1.25, shedZ);
        const shedMat = new BABYLON.StandardMaterial("shedMat", scene);
        shedMat.diffuseColor = new BABYLON.Color3(0.5, 0.38, 0.28);
        shedBase.material = shedMat;
        freeze(shedBase);

        // Shed roof (lean-to style)
        const shedRoof = BABYLON.MeshBuilder.CreateBox("shedRoof", {
            width: 3.8, height: 0.15, depth: 5.4
        }, scene);
        shedRoof.position.set(shedX, 2.6, shedZ);
        const shedRoofMat = new BABYLON.StandardMaterial("shedRoofMat", scene);
        shedRoofMat.diffuseColor = new BABYLON.Color3(0.35, 0.28, 0.22);
        shedRoof.material = shedRoofMat;
        freeze(shedRoof);

        // Door
        const shedDoor = BABYLON.MeshBuilder.CreateBox("shedDoor", {
            width: 0.05, height: 1.9, depth: 1.0
        }, scene);
        shedDoor.position.set(shedX - 1.78, 0.95, shedZ + 1.0);
        shedDoor.material = woodMat;
        freeze(shedDoor);

        // Lawnmower parked outside shed
        const mowerBody = BABYLON.MeshBuilder.CreateBox("lawnmower", {
            width: 0.9, height: 0.45, depth: 1.4
        }, scene);
        mowerBody.position.set(shedX - 2.5, 0.22, shedZ + 2.5);
        const mowerMat = new BABYLON.StandardMaterial("mowerMat", scene);
        mowerMat.diffuseColor = new BABYLON.Color3(0.0, 0.45, 0.0);
        mowerBody.material = mowerMat;
        freeze(mowerBody);

        // Mower wheels
        for (const wz of [-0.45, 0.45]) {
            for (const wx of [-0.35, 0.35]) {
                const wheel = BABYLON.MeshBuilder.CreateCylinder(`mWheel_${wz}_${wx}`, {
                    height: 0.12, diameter: 0.28, tessellation: 10
                }, scene);
                wheel.rotation.z = Math.PI / 2;
                wheel.position.set(shedX - 2.5 + wx, 0.14, shedZ + 2.5 + wz);
                wheel.material = darkMat;
                freeze(wheel);
            }
        }

        // ── 10. PERIMETER SAFETY BARRIER (low padded barrier boards) ─────────
        //   1 m high padded barriers between pitch and stands, along the long sides
        const barrierMat = new BABYLON.StandardMaterial("barrierMat", scene);
        barrierMat.diffuseColor = new BABYLON.Color3(0.14, 0.14, 0.18);

        const barrierY = halfW + 2.2; // just inside the first advertising board
        const barrierH = 1.0;
        const barrierT = 0.2;

        for (const sideX of [-1, 1]) {
            const bx = sideX * barrierY;
            const barrier = BABYLON.MeshBuilder.CreateBox(`barrier_${sideX}`, {
                width: barrierT, height: barrierH, depth: FIELD_LENGTH - 2
            }, scene);
            barrier.position.set(bx, barrierH / 2, 0);
            barrier.material = barrierMat;
            freeze(barrier);

            // Padding stripe on barrier (coloured strip at top)
            const padStripe = BABYLON.MeshBuilder.CreateBox(`barrierStripe_${sideX}`, {
                width: barrierT + 0.01, height: 0.12, depth: FIELD_LENGTH - 2
            }, scene);
            padStripe.position.set(bx, barrierH - 0.06, 0);
            const stripeMat = new BABYLON.StandardMaterial(`barrierStripeMat_${sideX}`, scene);
            stripeMat.diffuseColor = sideX === -1
                ? new BABYLON.Color3(0.8, 0.08, 0.08)
                : new BABYLON.Color3(0.08, 0.1, 0.7);
            padStripe.material = stripeMat;
            freeze(padStripe);
        }
    }

    private static _createInvisibleWalls(scene: BABYLON.Scene): void {
        const WALL_HEIGHT = 100;  // Very tall walls to prevent ball escaping
        const WALL_THICKNESS = 2;
        for (const side of [-1, 1]) {
            const wall = BABYLON.MeshBuilder.CreateBox(`wallSide${side}`, { width: WALL_THICKNESS, height: WALL_HEIGHT, depth: FIELD_LENGTH + WALL_THICKNESS * 2 }, scene);
            wall.position.set(side * (FIELD_WIDTH / 2 + WALL_THICKNESS / 2), WALL_HEIGHT / 2, 0);
            wall.isVisible = false;
            wall.freezeWorldMatrix();
        }
        const goalGap = 7;
        for (const end of [-1, 1]) {
            const z = end * (FIELD_LENGTH / 2 + WALL_THICKNESS / 2);
            const sideWidth = (FIELD_WIDTH - goalGap) / 2;
            for (const lr of [-1, 1]) {
                const wall = BABYLON.MeshBuilder.CreateBox(`wallEnd${end}_${lr}`, { width: sideWidth, height: WALL_HEIGHT, depth: WALL_THICKNESS }, scene);
                wall.position.set(lr * (goalGap / 2 + sideWidth / 2), WALL_HEIGHT / 2, z);
                wall.isVisible = false;
                wall.freezeWorldMatrix();
            }
        }
    }

    public static createRoundedCrowd(scene: BABYLON.Scene): void {
        // Cleanup existing crowd if any
        RoundedStadium._crowdInstances.forEach(m => {
            if (m.name.startsWith("fan_")) m.dispose(); // Only dispose procedural ones, GLTF sources are managed
            else m.thinInstanceCount = 0; // Reset thin instances for GLTF sources
        });
        RoundedStadium._flags.forEach(f => f.dispose());
        RoundedStadium._flags = [];
        RoundedStadium._baseMatricesPerVar = [];
        RoundedStadium._crowdInstances = [];

        // DynamicTexture for face (fallback/procedural only)
        const faceTex = new BABYLON.DynamicTexture("faceTex", { width: 128, height: 128 }, scene, false);
        const fCtx = faceTex.getContext() as CanvasRenderingContext2D;
        fCtx.fillStyle = "#ddccbb"; fCtx.fillRect(0, 0, 128, 128);
        fCtx.fillStyle = "#2a1a0a"; fCtx.fillRect(22, 30, 28, 5); fCtx.fillRect(78, 30, 28, 5);
        fCtx.fillStyle = "#ffffff"; fCtx.beginPath(); fCtx.ellipse(36, 48, 10, 7, 0, 0, 6.28); fCtx.fill(); fCtx.beginPath(); fCtx.ellipse(92, 48, 10, 7, 0, 0, 6.28); fCtx.fill();
        fCtx.fillStyle = "#1a1a1a"; fCtx.beginPath(); fCtx.arc(36, 48, 4, 0, 6.28); fCtx.fill(); fCtx.beginPath(); fCtx.arc(92, 48, 4, 0, 6.28); fCtx.fill();
        fCtx.strokeStyle = "rgba(120,40,40,0.7)"; fCtx.lineWidth = 3; fCtx.beginPath(); fCtx.arc(64, 82, 14, 0.2, 2.94); fCtx.stroke();
        faceTex.update();

        const skinTones = [new BABYLON.Color3(0.87, 0.72, 0.58), new BABYLON.Color3(0.45, 0.34, 0.27), new BABYLON.Color3(0.76, 0.6, 0.5)];
        const jerseyColors = [["red", new BABYLON.Color3(0.9, 0.05, 0.05)], ["white", new BABYLON.Color3(0.95, 0.95, 0.95)], ["away", new BABYLON.Color3(0.05, 0.05, 0.8)]];

        // Helper to create mesh (procedural fallback)
        const createFanMesh = (name: string, jerseyColor: BABYLON.Color3, skinColor: BABYLON.Color3): BABYLON.Mesh => {
            const torso = BABYLON.MeshBuilder.CreateBox(`${name}_t`, { width: 0.22, height: 0.3, depth: 0.12 }, scene); torso.position.y = 0.15;
            const head = BABYLON.MeshBuilder.CreateSphere(`${name}_h`, { diameter: 0.18 }, scene); head.position.y = 0.38;
            const headMat = new BABYLON.StandardMaterial(`${name}_hMat`, scene); headMat.diffuseColor = skinColor; headMat.diffuseTexture = faceTex; head.material = headMat;
            const bodyMat = new BABYLON.StandardMaterial(`${name}_bMat`, scene); bodyMat.diffuseColor = jerseyColor; torso.material = bodyMat;
            const merged = BABYLON.Mesh.MergeMeshes([torso, head], true, true, undefined, false, true)!;
            merged.name = name; merged.isVisible = false; merged.material = bodyMat;
            return merged;
        };

        RoundedStadium._variations = [];
        if (RoundedStadium._crowdSourceMeshes.length > 0) {
            for (let i = 0; i < RoundedStadium._crowdSourceMeshes.length; i++) {
                const mesh = RoundedStadium._crowdSourceMeshes[i];
                RoundedStadium._variations.push({ mesh, baseYs: [], count: 0 });
            }
        } else {
            for (let j = 0; j < jerseyColors.length; j++) {
                const skin = skinTones[j % skinTones.length];
                const mesh = createFanMesh(`fan_${jerseyColors[j][0]}`, jerseyColors[j][1] as BABYLON.Color3, skin);
                RoundedStadium._variations.push({ mesh, baseYs: [], count: 0 });
            }
        }

        const flagMesh = BABYLON.MeshBuilder.CreatePlane("flagBase", { width: 1.5, height: 1.0, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        flagMesh.isVisible = false;
        const flagMat = new BABYLON.StandardMaterial("flagMat", scene); flagMat.diffuseColor = new BABYLON.Color3(0.8, 0.1, 0.1); flagMat.backFaceCulling = false;

        const tiers = [{ y: 2, depth: 12, offset: 10, rows: 6 }, { y: 8, depth: 14, offset: 22, rows: 8 }, { y: 16, depth: 18, offset: 36, rows: 6 }];

        const populatePath = (pathPoints: BABYLON.Vector3[], tierIdx: number) => {
            let totalLen = 0; const segLens = [];
            for (let i = 0; i < pathPoints.length; i++) {
                const dist = BABYLON.Vector3.Distance(pathPoints[i], pathPoints[(i + 1) % pathPoints.length]);
                segLens.push(dist); totalLen += dist;
            }
            const fanSpacing = 0.7; // Increased spacing for realistic look
            const fanCount = Math.floor(totalLen / fanSpacing);
            let currentDist = 0; let currentSegIdx = 0;

            for (let i = 0; i < fanCount; i++) {
                const targetDist = i * fanSpacing;
                while (currentSegIdx < segLens.length && targetDist > currentDist + segLens[currentSegIdx]) { currentDist += segLens[currentSegIdx]; currentSegIdx++; }
                if (currentSegIdx >= segLens.length) break;
                const p1 = pathPoints[currentSegIdx]; const p2 = pathPoints[(currentSegIdx + 1) % pathPoints.length];
                const t = (targetDist - currentDist) / segLens[currentSegIdx];
                const pos = BABYLON.Vector3.Lerp(p1, p2, t);
                const center = new BABYLON.Vector3(0, pos.y, 0);

                // Density randomization
                if (Math.random() > 0.8) continue;

                const varIdx = Math.floor(Math.random() * RoundedStadium._variations.length);
                const rotY = Math.atan2(center.x - pos.x, center.z - pos.z);

                // Randomize scale for variety
                const scale = 0.95 + Math.random() * 0.15;
                const m = BABYLON.Matrix.Compose(
                    new BABYLON.Vector3(scale, scale, scale),
                    BABYLON.Quaternion.RotationYawPitchRoll(rotY + (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.05, 0),
                    pos
                );

                RoundedStadium._matricesPerVar[varIdx].push(m);
                RoundedStadium._baseYsPerVar[varIdx].push(pos.y);

                if (tierIdx === 0 && Math.random() > 0.92) {
                    const flag = flagMesh.createInstance(`flag_${i}`);
                    flag.position = pos.clone(); flag.position.y += 1.5; flag.rotation.y = rotY + 1.57; flag.material = flagMat;
                    RoundedStadium._flags.push(flag);
                }
            }
        };

        RoundedStadium._matricesPerVar = RoundedStadium._variations.map(() => []);
        RoundedStadium._baseYsPerVar = RoundedStadium._variations.map(() => []);

        for (let t = 0; t < tiers.length; t++) {
            const tier = tiers[t];
            for (let r = 0; r < tier.rows; r++) {
                const pathPoints = RoundedStadium._getRoundedPath(tier.offset - 2 + r * 0.8, tier.y + (r / tier.rows) * 6 + 0.5);
                populatePath(pathPoints, t);
            }
        }

        RoundedStadium._crowdInstances = [];
        for (let v = 0; v < RoundedStadium._variations.length; v++) {
            const mats = RoundedStadium._matricesPerVar[v];
            if (mats.length === 0) continue;
            const mesh = RoundedStadium._variations[v].mesh;
            mesh.isVisible = true;
            const buf = new Float32Array(mats.length * 16);
            for (let i = 0; i < mats.length; i++) mats[i].copyToArray(buf, i * 16);
            mesh.thinInstanceSetBuffer("matrix", buf, 16, false);
            RoundedStadium._baseMatricesPerVar.push(new Float32Array(buf));
            RoundedStadium._variations[v].baseYs = RoundedStadium._baseYsPerVar[v];
            RoundedStadium._variations[v].count = mats.length;
            RoundedStadium._crowdInstances.push(mesh);
        }
    }

    // Temporary storage for generation
    private static _matricesPerVar: BABYLON.Matrix[][] = [];
    private static _baseYsPerVar: number[][] = [];
}
